import React, {useMemo} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MapIcon from "@mui/icons-material/Map";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import VideocamIcon from "@mui/icons-material/Videocam";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import {useNavigate} from "react-router";
import {useInstructorTrainings} from "../firebase/useInstructorTrainings";
import {getComputedTrainingStatus, isDeadlinePassed} from "../calendar/trainingValidation";

const HIGHLIGHTS = [
    {
        icon: <SchoolIcon sx={{fontSize: 34, color: "primary.main"}} />,
        title: "Sertifisert kurslederopplæring",
        description:
            "Bli sertifisert kursholder i et helhetlig kurskonsept som skiller seg ut ved å forene metakognitive, mindfulnessbaserte og emosjonsfokuserte terapiformer.",
    },
    {
        icon: <MenuBookIcon sx={{fontSize: 34, color: "primary.main"}} />,
        title: "Komplett kursmateriell",
        description:
            "Ferdig utarbeidede presentasjoner (PowerPoint) til kursholderne og egne arbeidsbøker til kursdeltakerne som sikrer struktur og solid kvalitet i kurstilbudet.",
    },
    {
        icon: <MapIcon sx={{fontSize: 34, color: "primary.main"}} />,
        title: "Nasjonalt kursholderkart",
        description:
            "Sertifiserte instruktører får tilgang til vårt faglige nettverk og profilering i den nasjonale oversikten over godkjente kursholdere.",
    },
];

export const ProfessionalsSection = () => {
    const navigate = useNavigate();
    const {trainings} = useInstructorTrainings();

    const nextTraining = useMemo(() => {
        if (!Array.isArray(trainings) || trainings.length === 0) return null;
        // Prioritize upcoming training with open deadline
        const openUpcoming = trainings.find(
            (t) => t.published !== false && t.status !== "completed" && !isDeadlinePassed(t.registrationDeadline)
        );
        return openUpcoming || trainings.find((t) => t.published !== false) || trainings[0];
    }, [trainings]);

    const statusConfig = nextTraining ? getComputedTrainingStatus(nextTraining) : null;

    return (
        <Box
            component="section"
            id="for-fagfolk"
            aria-labelledby="professionals-title"
            sx={{
                py: {xs: 4, md: 6},
                width: "100%",
                maxWidth: "100%",
            }}
        >
            {/* Section Header */}
            <Box sx={{textAlign: "center", mb: 4, maxWidth: 760, mx: "auto"}}>
                <Typography
                    id="professionals-title"
                    variant="h4"
                    component="h2"
                    sx={{
                        fontWeight: 800,
                        fontSize: {xs: "1.6rem", sm: "2rem", md: "2.25rem"},
                        color: "primary.main",
                        mb: 1.5,
                        letterSpacing: "-0.01em",
                    }}
                >
                    For fagfolk og kursholdere
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{fontSize: {xs: "0.975rem", sm: "1.05rem"}, lineHeight: 1.7}}
                >
                    Ønsker du eller din arbeidsplass å tilby Kurs i Bekymringsmestring? Vi tilbyr godkjent opplæring for fagfolk og behandlere i kommunehelsetjenesten, DPS og spesialisthelsetjenesten over hele landet.
                </Typography>
            </Box>

            {/* Spotlight Banner: Next Planned Kurslederopplæring */}
            {nextTraining && (
                <Card
                    elevation={0}
                    sx={{
                        mb: 4,
                        p: {xs: 2.5, sm: 3.5},
                        borderRadius: 3,
                        border: "2px solid",
                        borderColor: "rgba(40, 82, 56, 0.25)",
                        bgcolor: "rgba(40, 82, 56, 0.03)",
                        boxShadow: "0 6px 20px rgba(40, 82, 56, 0.06)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: {xs: "column", md: "row"},
                            alignItems: {xs: "flex-start", md: "center"},
                            justifyContent: "space-between",
                            gap: 2.5,
                        }}
                    >
                        <Box sx={{flex: 1}}>
                            <Box sx={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1}}>
                                <Chip
                                    icon={<EventAvailableIcon />}
                                    label={statusConfig?.isClosed ? "Pågående kursrekke" : "Neste kurslederopplæring"}
                                    color={statusConfig?.isClosed ? "default" : "primary"}
                                    size="small"
                                    sx={{fontWeight: 700}}
                                />
                                <Chip
                                    icon={<VideocamIcon />}
                                    label={nextTraining.format === "digital" ? "Digitalt (Zoom)" : "Fysisk"}
                                    variant="outlined"
                                    size="small"
                                    sx={{fontWeight: 600}}
                                />
                                {statusConfig && (
                                    <Chip
                                        label={statusConfig.label}
                                        color={statusConfig.color}
                                        size="small"
                                        sx={{fontWeight: 700}}
                                    />
                                )}
                            </Box>

                            <Typography variant="h5" component="h3" sx={{fontWeight: 800, color: "text.primary", mb: 0.5}}>
                                {nextTraining.title}
                            </Typography>

                            {nextTraining.subtitle && (
                                <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                                    {nextTraining.subtitle}
                                </Typography>
                            )}

                            {Array.isArray(nextTraining.sessions) && nextTraining.sessions.length > 0 && (
                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1}}>
                                    {nextTraining.sessions.map((s, idx) => (
                                        <Typography key={idx} variant="caption" sx={{fontWeight: 600, color: "primary.main"}}>
                                            {s.sessionNumber}. samling: {s.dates}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <Box sx={{display: "flex", flexDirection: "column", gap: 1, minWidth: {sm: 220}}}>
                            <Button
                                variant="contained"
                                color="primary"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate("/calendar?tab=fagfolk")}
                                sx={{
                                    minHeight: 44,
                                    fontWeight: 700,
                                    textTransform: "none",
                                    borderRadius: 2,
                                    px: 3,
                                }}
                            >
                                {statusConfig?.isClosed ? "Se kurskalender & neste kull" : "Se datoer og påmelding"}
                            </Button>
                        </Box>
                    </Box>
                </Card>
            )}

            {/* 3 Pillars for Professionals */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "repeat(3, minmax(0, 1fr))"},
                    gap: 3,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    mb: 4,
                }}
            >
                {HIGHLIGHTS.map((item, idx) => (
                    <Card
                        key={idx}
                        elevation={0}
                        sx={{
                            height: "100%",
                            border: "1px solid",
                            borderColor: "rgba(40, 82, 56, 0.12)",
                            borderRadius: 3,
                            bgcolor: "background.paper",
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                                transform: "translateY(-3px)",
                                boxShadow: "0 8px 24px rgba(40, 82, 56, 0.08)",
                            },
                        }}
                    >
                        <CardContent sx={{p: {xs: 2.5, sm: 3}}}>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(40, 82, 56, 0.08)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 2,
                                }}
                            >
                                {item.icon}
                            </Box>
                            <Typography
                                variant="h6"
                                component="h3"
                                sx={{fontWeight: 700, mb: 1, color: "text.primary"}}
                            >
                                {item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.65}}>
                                {item.description}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* CTAs */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {xs: "column", sm: "row"},
                    justifyContent: "center",
                    gap: 2,
                    mb: 5,
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/get-educated")}
                    sx={{
                        fontWeight: 700,
                        px: {xs: 2, sm: 3.5},
                        py: 1.25,
                        borderRadius: 2.5,
                        width: {xs: "100%", sm: "auto"},
                        maxWidth: "100%",
                        whiteSpace: {xs: "normal", sm: "nowrap"},
                    }}
                >
                    Les mer om opplæringen
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<CalendarMonthIcon />}
                    onClick={() => navigate("/calendar?tab=fagfolk")}
                    sx={{
                        fontWeight: 700,
                        px: {xs: 2, sm: 3.5},
                        py: 1.25,
                        borderRadius: 2.5,
                        width: {xs: "100%", sm: "auto"},
                        maxWidth: "100%",
                        whiteSpace: {xs: "normal", sm: "nowrap"},
                    }}
                >
                    Åpne kurskalender
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => navigate("/order-books")}
                    sx={{
                        fontWeight: 700,
                        px: {xs: 2, sm: 3.5},
                        py: 1.25,
                        borderRadius: 2.5,
                        width: {xs: "100%", sm: "auto"},
                        maxWidth: "100%",
                        whiteSpace: {xs: "normal", sm: "nowrap"},
                    }}
                >
                    Bestill kursbøker
                </Button>
            </Box>
        </Box>
    );
};
